import { useIsMobile } from '@/hooks/use-mobile'
import { useResizeObserver } from '@/hooks/useResizeObserver'
import { useShortcut } from '@/lib/useShortcut'
import { cn } from '@/lib/utils'
import * as React from 'react'
import { useSidebar } from './sidebar-context'

const STORAGE_KEY_OPEN = 'toolbarOpen'
const STORAGE_KEY_HEIGHT = 'toolbarHeight'

const TOOLBAR_MIN_HEIGHT = 44
const TOOLBAR_MAX_HEIGHT = 96
const TOOLBAR_DEFAULT_HEIGHT = 56

function clampHeight(value: number) {
	return Math.min(TOOLBAR_MAX_HEIGHT, Math.max(TOOLBAR_MIN_HEIGHT, value))
}

const SLIDE = 'transform 250ms ease-out'
const FLOAT_IN = 'transform 340ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 160ms ease'
const FLOAT_OUT = 'transform 240ms cubic-bezier(0.4, 0, 0.6, 1), opacity 200ms ease'

interface ToolbarProps extends React.ComponentProps<'header'> {
	/**
	 * Collapses the strip regardless of the user's own choice, for a page that has nothing to put in
	 * it. Unlike a user collapse this offers no hover zone, so an empty strip cannot be summoned back.
	 */
	forceCollapsed?: boolean
	/**
	 * Receives the element pages portal their own rows into, stacked under the header row. Called with
	 * null on mobile and on unmount. Must be stable.
	 */
	extrasSlotRef?: (element: HTMLElement | null) => void
	/** Hands out the imperative opener once, so something outside (the `/` shortcut) can expand a collapsed strip. */
	onOpenControlReady?: (open: (() => void) | null) => void
}

/**
 * The strip above the page content, resizable and collapsible like the sidebar: drag its bottom edge
 * to resize, drag past the minimum to collapse, then hover the top edge to float it back over the
 * content and click it to reopen for good.
 *
 * Children render once. The strip morphs between flush and floating rather than mounting a second
 * copy, because the page header inside it owns a portal slot and reports its own emptiness upward.
 */
export function Toolbar({
	forceCollapsed = false,
	extrasSlotRef,
	onOpenControlReady,
	className,
	children,
	...props
}: ToolbarProps) {
	const isMobile = useIsMobile()
	const { isOpen: isSidebarOpen, openSidebar, closeSidebar } = useSidebar()

	const [isOpen, setIsOpenInternal] = React.useState<boolean>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_OPEN)
		return stored !== null ? stored === 'true' : true
	})

	const setIsOpen = React.useCallback((value: boolean) => {
		setIsOpenInternal(value)
		localStorage.setItem(STORAGE_KEY_OPEN, String(value))
	}, [])

	const [height, setHeightInternal] = React.useState<number>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_HEIGHT)
		const parsed = stored ? Number.parseInt(stored, 10) : TOOLBAR_DEFAULT_HEIGHT
		return Number.isNaN(parsed) ? TOOLBAR_DEFAULT_HEIGHT : clampHeight(parsed)
	})

	const setHeight = React.useCallback((h: number) => {
		const clamped = clampHeight(h)
		setHeightInternal(clamped)
		localStorage.setItem(STORAGE_KEY_HEIGHT, String(clamped))
	}, [])

	const [isFloating, setIsFloating] = React.useState(false)

	// ── Extras slot ───────────────────────────────────────────────────────────
	const [extrasEl, setExtrasEl] = React.useState<HTMLDivElement | null>(null)
	const [extrasHeight, setExtrasHeight] = React.useState(0)

	const setExtrasSlot = React.useCallback(
		(element: HTMLDivElement | null) => {
			setExtrasEl(element)
			extrasSlotRef?.(element)
		},
		[extrasSlotRef],
	)

	React.useEffect(() => {
		setExtrasHeight(extrasEl?.offsetHeight ?? 0)
	}, [extrasEl])

	// offsetHeight, not contentRect: the slot carries padding, which the content box leaves out.
	useResizeObserver(extrasEl, () => setExtrasHeight(extrasEl?.offsetHeight ?? 0))

	const openToolbar = React.useCallback(() => {
		setIsFloating(false)
		setIsOpen(true)
	}, [setIsOpen])

	const closeToolbar = React.useCallback(() => {
		setIsOpen(false)
	}, [setIsOpen])

	/**
	 * The variant handed to callers outside the strip (currently just the `/` shortcut), which opens it
	 * and immediately needs its final geometry to position something it reveals. The normal `openToolbar`
	 * slides over 250ms, so reading a rect right after calling it lands mid-animation; suppressing the
	 * transition for one frame here skips straight to the open position instead. Internal toggles
	 * (Mod+J, zen mode, the floating preview's click) keep the animated `openToolbar`.
	 */
	const openToolbarInstantly = React.useCallback(() => {
		setSuppressTransition(true)
		setIsFloating(false)
		setIsOpen(true)
		requestAnimationFrame(() => setSuppressTransition(false))
	}, [setIsOpen])

	React.useEffect(() => {
		onOpenControlReady?.(openToolbarInstantly)
		return () => onOpenControlReady?.(null)
	}, [onOpenControlReady, openToolbarInstantly])

	/**
	 * Zen mode keeps no state of its own: it collapses the strip and the sidebar while either is open,
	 * and reopens both once neither is. Each then returns to the height or width the user last gave it,
	 * and nothing can drift out of step with the panels' own controls.
	 */
	const toggleZenMode = React.useCallback(() => {
		if (isOpen || isSidebarOpen) {
			closeToolbar()
			closeSidebar()
			return
		}
		openToolbar()
		openSidebar()
	}, [isOpen, isSidebarOpen, openToolbar, closeToolbar, openSidebar, closeSidebar])

	const toggleToolbar = React.useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault()
			if (isOpen) {
				closeToolbar()
			} else {
				openToolbar()
			}
		},
		[isOpen, openToolbar, closeToolbar],
	)
	useShortcut('header.toggle', toggleToolbar, { skipInOverlay: true })

	const onToggleZenMode = React.useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault()
			toggleZenMode()
		},
		[toggleZenMode],
	)
	useShortcut('focusMode.toggle', onToggleZenMode, { skipInOverlay: true })

	// ── Resize drag ───────────────────────────────────────────────────────────
	const [suppressTransition, setSuppressTransition] = React.useState(false)

	const onResizeMouseDown = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			const startY = e.clientY
			const startHeight = height
			setSuppressTransition(true)
			document.body.style.cursor = 'row-resize'
			document.body.style.userSelect = 'none'

			const onMouseMove = (me: MouseEvent) => {
				const newHeight = startHeight + (me.clientY - startY)
				if (newHeight < TOOLBAR_MIN_HEIGHT) {
					closeToolbar()
					cleanup()
				} else {
					setHeight(newHeight)
				}
			}

			const cleanup = () => {
				document.body.style.cursor = ''
				document.body.style.userSelect = ''
				setSuppressTransition(false)
				document.removeEventListener('mousemove', onMouseMove)
				document.removeEventListener('mouseup', cleanup)
			}

			document.addEventListener('mousemove', onMouseMove)
			document.addEventListener('mouseup', cleanup)
		},
		[height, setHeight, closeToolbar],
	)

	// ── Hover-float ───────────────────────────────────────────────────────────
	const hoverZoneRef = React.useRef<HTMLDivElement>(null)
	const toolbarRef = React.useRef<HTMLElement>(null)

	const handleFloatLeave = React.useCallback((e: React.MouseEvent) => {
		const related = e.relatedTarget as Node | null
		if (toolbarRef.current?.contains(related) || hoverZoneRef.current?.contains(related)) {
			return
		}
		setIsFloating(false)
	}, [])

	const handleFloatClick = React.useCallback(
		(e: React.MouseEvent) => {
			const target = e.target as HTMLElement
			const isInteractive = target.closest('a, button, [role="button"]')
			if (!isInteractive) {
				openToolbar()
			}
		},
		[openToolbar],
	)

	// ── Mobile ────────────────────────────────────────────────────────────────
	// No extras slot here, so a page keeps its own rows in the page: the mobile strip is pinned to 40px
	// with overflow hidden (the mobile block in index.css) and would clip anything stacked under it.
	if (isMobile) {
		return (
			<header
				data-slot='toolbar'
				data-state={forceCollapsed ? 'collapsed' : 'expanded'}
				data-testid='app-toolbar'
				className={cn(
					'flex shrink-0 items-center overflow-hidden transition-[height] ease-linear',
					forceCollapsed ? 'min-h-0' : 'min-h-14',
					className,
				)}
				{...props}>
				{children}
			</header>
		)
	}

	// ── Desktop ───────────────────────────────────────────────────────────────
	const isExpanded = isOpen && !forceCollapsed
	const canFloat = !isOpen && !forceCollapsed
	const isVisible = isExpanded || (canFloat && isFloating)
	const stripHeight = height + extrasHeight

	const transition = suppressTransition ? 'none' : isOpen ? SLIDE : isFloating ? FLOAT_IN : FLOAT_OUT

	return (
		<>
			{/* Holds the strip's place in the column; the strip itself is lifted out of flow so it can float. */}
			<div
				aria-hidden
				data-slot='toolbar-spacer'
				style={{
					height: isExpanded ? `${stripHeight}px` : '0px',
					flexShrink: 0,
					transition: suppressTransition ? 'none' : 'height 250ms ease-out',
				}}
			/>

			<header
				ref={toolbarRef}
				data-slot='toolbar'
				data-state={isExpanded ? 'expanded' : 'collapsed'}
				data-testid='app-toolbar'
				aria-label={canFloat ? 'Toolbar preview' : undefined}
				onMouseLeave={canFloat ? handleFloatLeave : undefined}
				onClick={canFloat ? handleFloatClick : undefined}
				className={cn(
					'absolute top-0 left-0 right-0 flex flex-col overflow-hidden bg-sidebar',
					canFloat && 'mt-2 mx-2 rounded-md border border-sidebar-border shadow-levitating cursor-pointer',
					className,
				)}
				style={{
					// Above the sticky table header (z-20) it floats over, below the sidebar's own float (z-30).
					zIndex: 25,
					height: `${stripHeight}px`,
					transform: isVisible ? 'translateY(0)' : 'translateY(calc(-100% - 0.75rem))',
					opacity: isVisible ? 1 : 0,
					pointerEvents: isVisible ? 'auto' : 'none',
					transition,
					willChange: 'transform',
				}}
				{...props}>
				<div className='flex shrink-0 items-center overflow-hidden' style={{ height: `${height}px` }}>
					{children}
				</div>

				{/* Rows a page portals in: filter chips and the like, whatever else sits above its content. */}
				<div
					ref={setExtrasSlot}
					data-slot='toolbar-extras'
					data-testid='app-toolbar-extras'
					className='shrink-0 empty:hidden px-4 pb-2'
				/>

				{isExpanded && (
					<div
						data-testid='app-toolbar-resize-handle'
						onMouseDown={onResizeMouseDown}
						style={{
							position: 'absolute',
							left: 0,
							bottom: 0,
							width: '100%',
							height: '4px',
							cursor: 'row-resize',
							zIndex: 30,
						}}
					/>
				)}
			</header>

			{canFloat && (
				<div
					ref={hoverZoneRef}
					data-testid='app-toolbar-edge-zone'
					onMouseEnter={() => setIsFloating(true)}
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '4px',
						zIndex: 25,
					}}
				/>
			)}
		</>
	)
}
